/**
 * Las cuentas de Facturación en la pantalla (7/10/2026)
 * ---------------------------------------------------
 * Es LA MISMA cuenta que `backend/services/facturacion_fiscal.py` (la de
 * FactuAI): la base de cada línea redondeada al céntimo y la cuota POR GRUPO
 * de IVA; las líneas con el IVA dentro (los cobros de pacientes) entran en su
 * grupo por el total. Aquí solo se enseña mientras se escribe: lo que se
 * guarda lo calcula el servidor.
 */

/** Redondeo al céntimo, la mitad lejos del cero (como ROUND_HALF_UP en Python). */
export function r2(x) {
  const n = Number(x || 0);
  if (!Number.isFinite(n)) return 0;
  const signo = n < 0 ? -1 : 1;
  return (signo * Math.floor(Math.abs(n) * 100 + 0.5 + 1e-7)) / 100;
}

/** «1.250,50», «1250,5» o «1250.5» → número. */
export function num(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  let t = String(v ?? '').replace(/€|\s/g, '').trim();
  if (/^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(t)) t = t.replace(/\./g, '').replace(',', '.');
  else t = t.replace(',', '.');
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : 0;
}

export const CAUSAS = {
  E1: 'Exenta (art. 20 de la Ley del IVA)',
  E2: 'Exenta (art. 21 de la Ley del IVA)',
  E3: 'Exenta (art. 22 de la Ley del IVA)',
  E4: 'Exenta (arts. 23 y 24 de la Ley del IVA)',
  E5: 'Exenta (art. 25 de la Ley del IVA)',
  E6: 'Exenta (otros supuestos de la Ley del IVA)',
};

export function textoDeGrupo(g) {
  if (g.exenta) return CAUSAS[g.causa_exencion || 'E1'] || 'Exenta';
  return `IVA ${String(g.tipo_iva).replace('.', ',')} %`;
}

export function calcularFactura(lineas, irpfPct = 0) {
  const grupos = new Map();
  for (const l of lineas || []) {
    const cantidad = l.cantidad === '' || l.cantidad == null ? 1 : num(l.cantidad);
    const precio = num(l.precio);
    const dto = num(l.descuento_pct);
    const exenta = !!l.exenta;
    const tipo = exenta ? 0 : num(l.tipo_iva ?? 21);
    const importe = r2((cantidad * precio * (100 - dto)) / 100);
    const ivaIncluido = !!l.iva_incluido && !exenta && tipo > 0;
    const causa = exenta ? (l.causa_exencion || 'E1') : null;
    const clave = exenta ? `E|${causa}` : `I|${tipo}`;
    const g = grupos.get(clave) || { exenta, tipo_iva: tipo, causa_exencion: causa, baseSin: 0, totalCon: 0 };
    if (ivaIncluido) g.totalCon = r2(g.totalCon + importe);
    else g.baseSin = r2(g.baseSin + importe);
    grupos.set(clave, g);
  }
  const desglose = [...grupos.values()].map((g) => {
    if (g.exenta) return { ...g, base: r2(g.baseSin + g.totalCon), cuota: 0 };
    const cuotaSin = r2((g.baseSin * g.tipo_iva) / 100);
    const baseCon = g.totalCon ? r2(g.totalCon / (1 + g.tipo_iva / 100)) : 0;
    return { ...g, base: r2(g.baseSin + baseCon), cuota: r2(cuotaSin + g.totalCon - baseCon) };
  }).sort((a, b) => (a.exenta - b.exenta) || (b.tipo_iva - a.tipo_iva));
  const base = r2(desglose.reduce((s, g) => s + g.base, 0));
  const cuota = r2(desglose.reduce((s, g) => s + g.cuota, 0));
  const irpf = r2((base * num(irpfPct)) / 100);
  return { desglose, base_imponible: base, cuota_iva: cuota, irpf, total: r2(base + cuota - irpf) };
}

export function calcularGasto(lineas, retencionTipo = 'NINGUNA', retencionPct = 0) {
  let base = 0; let cuota = 0; let deducible = 0;
  for (const l of lineas || []) {
    const b = r2(num(l.base));
    const c = r2((b * num(l.tipo_iva ?? 21)) / 100);
    const pct = l.deducible_pct === '' || l.deducible_pct == null ? 100 : num(l.deducible_pct);
    base = r2(base + b);
    cuota = r2(cuota + c);
    deducible = r2(deducible + r2((c * pct) / 100));
  }
  const retencion = retencionTipo && retencionTipo !== 'NINGUNA' ? r2((base * num(retencionPct)) / 100) : 0;
  return { base, cuota_iva: cuota, cuota_deducible: deducible, retencion, total: r2(base + cuota - retencion) };
}

/** Trimestre (1-4) de hoy. */
export function trimestreDeHoy(d = new Date()) {
  return Math.floor(d.getMonth() / 3) + 1;
}

/** Las casillas de los modelos, con su nombre. */
export const CASILLAS_303 = {
  150: 'Base al 0 %', 151: 'Tipo', 152: 'Cuota',
  '01': 'Base al 4 %', '02': 'Tipo', '03': 'Cuota',
  '04': 'Base al 10 %', '05': 'Tipo', '06': 'Cuota',
  '07': 'Base al 21 %', '08': 'Tipo', '09': 'Cuota',
  14: 'Modificación de bases (rectificativas)', 15: 'Modificación de cuotas (rectificativas)',
  27: 'Total cuota devengada',
  28: 'IVA deducible: base (operaciones interiores corrientes)', 29: 'IVA deducible: cuota',
  40: 'Rectificación de deducciones: base', 41: 'Rectificación de deducciones: cuota',
  45: 'Total a deducir', 46: 'Resultado régimen general',
  59: 'Entregas intracomunitarias', 60: 'Exportaciones y asimiladas',
  64: 'Suma de resultados', 65: '% atribuible a la Administración del Estado',
  66: 'Atribuible a la Administración del Estado', 69: 'Resultado', 71: 'Resultado de la liquidación',
};

export const CASILLAS_111 = {
  '01': 'Rendimientos del trabajo: nº de perceptores', '02': 'Rendimientos del trabajo: importe',
  '03': 'Rendimientos del trabajo: retenciones',
  '07': 'Actividades económicas: nº de perceptores', '08': 'Actividades económicas: importe',
  '09': 'Actividades económicas: retenciones',
  28: 'Total retenciones', 30: 'Resultado a ingresar',
};

export const CASILLAS_115 = {
  '01': 'Nº de perceptores', '02': 'Base de las retenciones', '03': 'Retenciones (resultado a ingresar)',
};

/** Las casillas que son un número de perceptores o un porcentaje, no euros. */
export const NO_SON_EUROS = new Set(['02', '05', '08', '151', '65', '01-111', '07-111', '01-115']);
